/** The storage key of the hidden columns of one table. */
export const hiddenKey = (id: string) => `xflow.columns.${id}`;

/** The stored hidden keys that are still columns of the table. The first column never hides. */
export function parseHidden(raw: string | null, keys: readonly string[], off: readonly string[] = []): string[] {
  if (raw === null) return keys.slice(1).filter((key) => off.includes(key));
  let found: unknown;
  try {
    found = JSON.parse(raw ?? "[]");
  } catch {
    return [];
  }
  if (!Array.isArray(found)) return [];
  return keys.slice(1).filter((key) => found.includes(key));
}

/** One rule for each hidden column. Rows with a spanning cell (group headers, details) keep all cells. */
export function hiddenCss(id: string, keys: readonly string[], hidden: readonly string[]): string {
  return keys
    .map((key, index) => (index > 0 && hidden.includes(key) ? index + 1 : 0))
    .filter((child) => child > 0)
    .map((child) => `table[data-cols="${id}"] tr:not(:has(> [colspan])) > :nth-child(${child}) { display: none; }`)
    .join("\n");
}

/** Right-aligns each cell of a number column, so a value is always under its header; a phone card keeps the left. */
export function numCss(id: string, nums: readonly boolean[]): string {
  const cells = nums.map((num, index) => (num ? index + 1 : 0)).filter((child) => child > 0);
  if (!cells.length) return "";
  const rule = (table: string) =>
    cells.map((child) => `${table} tr:not(:has(> [colspan])) > :nth-child(${child}) { text-align: right; font-variant-numeric: tabular-nums; }`).join("\n");
  return `@media (min-width: 40.01rem) {\n${rule(`table.list[data-cols="${id}"]`)}\n}\n${rule(`table:not(.list)[data-cols="${id}"]`)}`;
}

/** The rem of a column with no weight in a table with weights, the least that it gets before the table scrolls (rule U-5). */
export const FREE_COLUMN_MIN = 9;

/** Each weight gives a share of the desktop width and 1rem of minimum width. With a column of no weight, each weight is a width in rem. */
export function widthCss(id: string, keys: readonly string[], weights: readonly (number | undefined)[], hidden: readonly string[]): string {
  if (!weights.some((weight) => weight)) return "";
  const shown = keys.map((key, index) => (index === 0 || !hidden.includes(key) ? (weights[index] ?? 0) : 0));
  const fixed = shown.reduce((sum, weight) => sum + weight, 0);
  const free = keys.some((key, index) => weights[index] === undefined && (index === 0 || !hidden.includes(key)));
  const total = free ? fixed + FREE_COLUMN_MIN : fixed;
  const size = (weight: number) => (free ? `${weight}rem` : `${((100 * weight) / total).toFixed(3)}%`);
  const cells = shown
    .map((weight, index) => (weight ? `table.list[data-cols="${id}"] > thead > tr > :nth-child(${index + 1}) { width: ${size(weight)}; }` : ""))
    .filter(Boolean);
  return `@media (min-width: 40.01rem) {\ntable.list[data-cols="${id}"] { table-layout: fixed; min-width: ${total}rem; }\n${cells.join("\n")}\n}`;
}
